import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Buildings, ListChecks, BellRinging, FloppyDisk, Hash, FileXls, Trash, Package, Factory, Lightning } from "@phosphor-icons/react";

const FIELD_CLS = "bg-zinc-950 border-zinc-800 rounded-sm text-zinc-100 focus:border-emerald-600 focus-visible:ring-emerald-600";

export default function CompanySettings() {
  const [s, setS] = useState(null);
  const [termsText, setTermsText] = useState("");
  const [saving, setSaving] = useState(false);
  const [nextPoNumber, setNextPoNumber] = useState(null);
  const [poInput, setPoInput] = useState("");
  const [poSaving, setPoSaving] = useState(false);
  const [poTemplate, setPoTemplate] = useState({ exists: false });
  const [poTemplateUploading, setPoTemplateUploading] = useState(false);
  const [templateItems, setTemplateItems] = useState(null);
  const [templateHasCorrections, setTemplateHasCorrections] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [digest, setDigest] = useState({ enabled: false, recipient_ids: [] });
  const [digestSaving, setDigestSaving] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [mfgAlerts, setMfgAlerts] = useState({ recipient_ids: [] });
  const [mfgAlertsSaving, setMfgAlertsSaving] = useState(false);
  const [orderTakers, setOrderTakers] = useState({ user_ids: [] });
  const [orderTakersSaving, setOrderTakersSaving] = useState(false);
  const [orderTakerReminder, setOrderTakerReminder] = useState({ enabled: false, threshold_days: 3 });
  const [orderTakerReminderSaving, setOrderTakerReminderSaving] = useState(false);
  const [slowThreshold, setSlowThreshold] = useState(48);
  const [slowThresholdSaving, setSlowThresholdSaving] = useState(false);
  const [iftt, setIftt] = useState({ connected: false, key_masked: null, events: {} });
  const [ifttKeyInput, setIfttKeyInput] = useState("");
  const [ifttSaving, setIfttSaving] = useState(false);
  const [ifttTesting, setIfttTesting] = useState(false);
  const [ifttDisconnecting, setIfttDisconnecting] = useState(false);

  useEffect(() => {
    api.get("/settings/overdue-digest").then((r) => setDigest(r.data)).catch(() => {});
    api.get("/users?active=true").then((r) => setTeamMembers(r.data)).catch(() => {});
    api.get("/settings/manufacturing-alerts").then((r) => setMfgAlerts(r.data)).catch(() => {});
    api.get("/settings/order-takers").then((r) => setOrderTakers(r.data)).catch(() => {});
    api.get("/settings/order-taker-reminder").then((r) => setOrderTakerReminder(r.data)).catch(() => {});
    api.get("/settings/stage-duration").then((r) => setSlowThreshold(r.data.slow_threshold_hours)).catch(() => {});
    api.get("/settings/ifttt").then((r) => setIftt(r.data)).catch(() => {});
  }, []);

  const toggleIfttEvent = (key) => setIftt((x) => ({ ...x, events: { ...x.events, [key]: !x.events[key] } }));
  const saveIftt = async () => {
    setIfttSaving(true);
    try {
      const { data } = await api.put("/settings/ifttt", { webhooks_key: ifttKeyInput || undefined, events: iftt.events });
      setIftt(data);
      setIfttKeyInput("");
      toast.success("IFTTT settings saved");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setIfttSaving(false); }
  };
  const testIftt = async () => {
    setIfttTesting(true);
    try { await api.post("/settings/ifttt/test"); toast.success("Test event sent — check your IFTTT applet"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setIfttTesting(false); }
  };
  const disconnectIftt = async () => {
    if (!window.confirm("Disconnect IFTTT? This clears your saved Webhooks key and turns off all events.")) return;
    setIfttDisconnecting(true);
    try { const { data } = await api.post("/settings/ifttt/disconnect"); setIftt(data); toast.success("IFTTT disconnected"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setIfttDisconnecting(false); }
  };

  useEffect(() => {
    api.get("/company-settings").then((r) => {
      setS(r.data);
      setTermsText((r.data.terms || []).join("\n"));
    }).catch(() => toast.error("Could not load company settings"));
    api.get("/admin/po-counter").then((r) => {
      setNextPoNumber(r.data.next_po_number);
      setPoInput(String(r.data.next_po_number));
    }).catch(() => toast.error("Could not load PO numbering"));
    api.get("/settings/po-template").then((r) => setPoTemplate(r.data)).catch(() => {});
    loadTemplateItems();
  }, []);

  const loadTemplateItems = () => {
    api.get("/settings/po-template/items").then((r) => {
      setTemplateItems(r.data.items || []);
      setTemplateHasCorrections(!!r.data.has_corrections);
    }).catch(() => setTemplateItems([]));
  };

  const uploadPoTemplate = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const data = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    setPoTemplateUploading(true);
    try {
      await api.put("/settings/po-template", { name: file.name, data });
      toast.success("PO template uploaded");
      setPoTemplate({ exists: true, name: file.name, uploaded_by: "you", uploaded_at: new Date().toISOString() });
      loadTemplateItems();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not upload — check it's a valid .xlsx file"); }
    finally { setPoTemplateUploading(false); }
  };

  const deletePoTemplate = async () => {
    if (!window.confirm("Remove the PO template?")) return;
    try { await api.delete("/settings/po-template"); toast.success("Template removed"); setPoTemplate({ exists: false }); setTemplateItems([]); setTemplateHasCorrections(false); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const updateTemplateItem = (idx, field, val) => setTemplateItems((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  const removeTemplateItem = (idx) => setTemplateItems((rows) => rows.filter((_, i) => i !== idx));
  const addTemplateItem = () => setTemplateItems((rows) => [...rows, { item: "", group: "", make: "", cat_no: "", qty: "", rate: "", dis: "" }]);
  const saveTemplateCorrections = async () => {
    setTemplateSaving(true);
    try {
      const { data } = await api.put("/settings/po-template/items", { items: templateItems.filter((r) => (r.item || "").trim()) });
      setTemplateItems(data.items);
      setTemplateHasCorrections(true);
      toast.success("Corrections saved — every new PO will now use these fixed rows");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTemplateSaving(false); }
  };
  const resetTemplateCorrections = async () => {
    if (!window.confirm("Discard your corrections and go back to auto-detected columns from the file?")) return;
    try { await api.delete("/settings/po-template/items"); toast.success("Reverted to auto-detected columns"); loadTemplateItems(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggleDigestRecipient = (uid) => setDigest((d) => ({
    ...d, recipient_ids: d.recipient_ids.includes(uid) ? d.recipient_ids.filter((x) => x !== uid) : [...d.recipient_ids, uid],
  }));
  const saveDigest = async () => {
    setDigestSaving(true);
    try {
      const { data } = await api.put("/settings/overdue-digest", digest);
      setDigest(data.ok ? digest : data);
      toast.success("Overdue Stage Digest settings saved");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setDigestSaving(false);
  };
  const sendTestDigest = async () => {
    try {
      const { data } = await api.post("/admin/run-overdue-digest");
      toast.success(data.digests_sent > 0 ? `Digest sent to ${data.digests_sent} recipient(s)` : "No overdue stages right now — nothing to send");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggleMfgRecipient = (uid) => setMfgAlerts((d) => ({
    ...d, recipient_ids: d.recipient_ids.includes(uid) ? d.recipient_ids.filter((x) => x !== uid) : [...d.recipient_ids, uid],
  }));
  const saveMfgAlerts = async () => {
    setMfgAlertsSaving(true);
    try { await api.put("/settings/manufacturing-alerts", mfgAlerts); toast.success("Manufacturing alert recipients saved"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setMfgAlertsSaving(false);
  };

  const toggleOrderTaker = (uid) => setOrderTakers((d) => ({
    ...d, user_ids: d.user_ids.includes(uid) ? d.user_ids.filter((x) => x !== uid) : [...d.user_ids, uid],
  }));
  const saveOrderTakers = async () => {
    setOrderTakersSaving(true);
    try { await api.put("/settings/order-takers", orderTakers); toast.success("Order Takers list saved"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setOrderTakersSaving(false);
  };

  const saveOrderTakerReminder = async () => {
    setOrderTakerReminderSaving(true);
    try { await api.put("/settings/order-taker-reminder", orderTakerReminder); toast.success("Order Taker Reminder settings saved"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setOrderTakerReminderSaving(false);
  };
  const sendTestOrderTakerReminder = async () => {
    try {
      const { data } = await api.post("/admin/run-order-taker-reminder");
      toast.success(data.recipients_notified > 0 ? `Reminder sent to ${data.recipients_notified} recipient(s)` : "A new order was logged recently enough — nothing to send");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const saveSlowThreshold = async () => {
    setSlowThresholdSaving(true);
    try { await api.put("/settings/stage-duration", { slow_threshold_hours: Number(slowThreshold) }); toast.success("Slow-stage threshold saved"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setSlowThresholdSaving(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/company-settings", {
        ...s,
        terms: termsText.split("\n").map((t) => t.trim()).filter(Boolean),
        nudge_days: Number(s.nudge_days) || 7,
      });
      setS(data);
      setTermsText((data.terms || []).join("\n"));
      toast.success("Company settings saved — all new PO documents will use them");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setSaving(false);
  };

  const savePoCounter = async () => {
    const n = Number(poInput);
    if (!n || n < 1) { toast.error("Enter a valid PO reference number"); return; }
    setPoSaving(true);
    try {
      const { data } = await api.put("/admin/po-counter", { next_po_number: n });
      setNextPoNumber(data.next_po_number);
      toast.success(`The next Purchase Order raised will be PO-${data.next_po_number}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setPoSaving(false);
  };

  if (!s) return <div className="p-8 text-sm text-zinc-500">Loading settings…</div>;
  const set = (k) => (e) => setS((x) => ({ ...x, [k]: e.target.value }));

  return (
    <div className="p-6 lg:p-8 max-w-3xl animate-fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-600 font-bold mb-1">Administration</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-zinc-50 flex items-center gap-2">
            <Buildings size={26} className="text-emerald-600" weight="bold" /> Company Settings
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Admin-only. These details appear on every PO document (PDF, Excel, print, email).</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="settings-save"
          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold gap-1.5">
          <FloppyDisk size={16} /> {saving ? "Saving…" : "Save Settings"}
        </Button>
      </div>

      <Tabs defaultValue="company">
        <TabsList>
          <TabsTrigger value="company" data-testid="settings-tab-company"><Buildings size={15} className="mr-1.5" />Company Details</TabsTrigger>
          <TabsTrigger value="terms" data-testid="settings-tab-terms"><ListChecks size={15} className="mr-1.5" />PO Terms &amp; Conditions</TabsTrigger>
          <TabsTrigger value="numbering" data-testid="settings-tab-numbering"><Hash size={15} className="mr-1.5" />PO Numbering</TabsTrigger>
          <TabsTrigger value="template" data-testid="settings-tab-template"><FileXls size={15} className="mr-1.5" />PO Template</TabsTrigger>
          <TabsTrigger value="followup" data-testid="settings-tab-followup"><BellRinging size={15} className="mr-1.5" />Follow-up &amp; Reminders</TabsTrigger>
          <TabsTrigger value="manufacturing" data-testid="settings-tab-manufacturing"><Factory size={15} className="mr-1.5" />Manufacturing Alerts</TabsTrigger>
          <TabsTrigger value="order-takers" data-testid="settings-tab-order-takers"><ListChecks size={15} className="mr-1.5" />Order Takers</TabsTrigger>
          <TabsTrigger value="store" data-testid="settings-tab-store"><Package size={15} className="mr-1.5" />Store Access</TabsTrigger>
          <TabsTrigger value="ifttt" data-testid="settings-tab-ifttt"><Lightning size={15} className="mr-1.5" />IFTTT Automation</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-5 space-y-4">
          {[
            ["name", "Company Name"],
            ["tagline", "Tagline (shown under the logo on PO documents)"],
            ["address", "Address"],
            ["phone", "Phone / Fax"],
            ["email", "Email"],
            ["gst_no", "GST No"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">{label}</label>
              <Input value={s[k] || ""} onChange={set(k)} className={FIELD_CLS} data-testid={`settings-${k}`} />
            </div>
          ))}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Delivery Address (where vendors should ship material — auto-fills every new PO's Delivery Address)</label>
            <Textarea rows={3} value={s.delivery_address || ""} onChange={set("delivery_address")} className={FIELD_CLS} data-testid="settings-delivery_address" />
          </div>
        </TabsContent>

        <TabsContent value="terms" className="mt-5">
          <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">
            Terms &amp; Conditions — one per line (numbered automatically on PO documents)
          </label>
          <Textarea rows={12} value={termsText} onChange={(e) => setTermsText(e.target.value)}
            className={`${FIELD_CLS} font-mono text-xs leading-relaxed`} data-testid="settings-terms" />
          <p className="text-xs text-zinc-500 mt-2">Used as the default for every new PO. Existing POs keep the terms they were created with.</p>
        </TabsContent>

        <TabsContent value="numbering" className="mt-5 space-y-4 max-w-sm">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Next PO reference number</label>
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono text-zinc-400">PO-</span>
              <Input type="number" min="1" value={poInput} onChange={(e) => setPoInput(e.target.value)}
                className={FIELD_CLS} data-testid="settings-po-next-number" />
              <Button onClick={savePoCounter} disabled={poSaving} data-testid="settings-po-save"
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold shrink-0">
                {poSaving ? "Saving…" : "Set"}
              </Button>
            </div>
            <p className="text-xs text-zinc-500 mt-2" data-testid="settings-po-current">
              {nextPoNumber ? `The next Purchase Order you raise will be numbered PO-${nextPoNumber}.` : "Loading current counter…"}
            </p>
            <p className="text-xs text-zinc-500 mt-2">
              PO counting increments automatically from here (PO-{nextPoNumber}, PO-{nextPoNumber ? nextPoNumber + 1 : ""}, …).
              Change this number any time — for example to continue from your previous system's last PO number. Already-raised POs are never renumbered.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="template" className="mt-5 space-y-4 max-w-4xl">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Blank PO Template</label>
            <p className="text-xs text-zinc-500 mb-3">
              Upload a blank Purchase Order Excel format for the Costing team to download as a starting point when raising a PO from an Order's Account Section.
            </p>
            {poTemplate?.exists ? (
              <div className="flex items-center justify-between gap-3 border border-zinc-800 bg-zinc-950 rounded-sm px-3 py-2.5 mb-3" data-testid="settings-po-template-current">
                <div className="text-xs">
                  <div className="text-zinc-100 font-semibold">{poTemplate.name}</div>
                  <div className="text-zinc-500 mt-0.5">Uploaded by {poTemplate.uploaded_by} · {(poTemplate.uploaded_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <Button size="sm" variant="outline" onClick={deletePoTemplate} className="border-zinc-800 text-red-400 hover:bg-red-500/10 rounded-sm shrink-0" data-testid="settings-po-template-delete">
                  <Trash size={14} className="mr-1" /> Remove
                </Button>
              </div>
            ) : (
              <p className="text-xs text-zinc-600 mb-3" data-testid="settings-po-template-none">No template uploaded yet.</p>
            )}
            <label className="inline-flex items-center gap-1.5 cursor-pointer bg-zinc-900 border border-zinc-800 hover:border-emerald-600 text-zinc-200 rounded-sm text-xs px-3 py-2">
              <FileXls size={15} /> {poTemplateUploading ? "Uploading…" : "Upload .xlsx Template"}
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={uploadPoTemplate} disabled={poTemplateUploading} data-testid="settings-po-template-upload" />
            </label>
          </div>

          {poTemplate?.exists && (
            <div className="border-t border-zinc-800 pt-4">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block">Auto-filled Items — review &amp; fix</label>
                  <p className="text-xs text-zinc-500 mt-1">
                    {templateHasCorrections
                      ? "Showing your saved corrections. This is exactly what new POs will auto-fill with."
                      : "Showing columns auto-detected from your file. Fix anything misread (e.g. a wrong Group/Rate) below, then save."}
                  </p>
                </div>
                {templateHasCorrections && (
                  <Button size="sm" variant="outline" onClick={resetTemplateCorrections} className="border-zinc-800 text-zinc-400 hover:text-red-400 rounded-sm text-xs shrink-0" data-testid="settings-template-items-reset">
                    Reset to Auto-Detected
                  </Button>
                )}
              </div>
              {templateItems === null ? (
                <p className="text-xs text-zinc-500">Loading…</p>
              ) : (
                <>
                  <div className="border border-zinc-800 rounded-sm max-h-80 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-zinc-950">
                        <tr className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 text-left">
                          <th className="px-2 py-2 w-8">#</th>
                          <th className="px-2 py-2 min-w-[160px]">Item</th>
                          <th className="px-2 py-2">Group</th>
                          <th className="px-2 py-2">Make</th>
                          <th className="px-2 py-2">Cat No</th>
                          <th className="px-2 py-2 text-right">Qty</th>
                          <th className="px-2 py-2 text-right">Rate</th>
                          <th className="px-2 py-2 text-right">Dis %</th>
                          <th className="px-2 py-2 w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-900">
                        {templateItems.map((row, i) => (
                          <tr key={row.id || i} data-testid={`settings-template-row-${i}`}>
                            <td className="px-2 py-1 text-zinc-600 font-mono">{i + 1}</td>
                            {["item", "group", "make", "cat_no"].map((f) => (
                              <td key={f} className="px-1 py-1">
                                <input value={row[f] || ""} onChange={(e) => updateTemplateItem(i, f, e.target.value)}
                                  className="w-full bg-transparent border border-transparent hover:border-zinc-800 focus:border-emerald-600 focus:outline-none rounded-sm px-1.5 py-1 text-zinc-100"
                                  data-testid={`settings-template-${f}-${i}`} />
                              </td>
                            ))}
                            {["qty", "rate", "dis"].map((f) => (
                              <td key={f} className="px-1 py-1">
                                <input value={row[f] ?? ""} onChange={(e) => updateTemplateItem(i, f, e.target.value)}
                                  className="w-full bg-transparent border border-transparent hover:border-zinc-800 focus:border-emerald-600 focus:outline-none rounded-sm px-1.5 py-1 text-right text-zinc-100"
                                  data-testid={`settings-template-${f}-${i}`} />
                              </td>
                            ))}
                            <td className="px-1 py-1 text-center">
                              <button type="button" onClick={() => removeTemplateItem(i)} className="text-zinc-600 hover:text-red-400" data-testid={`settings-template-remove-${i}`}><Trash size={12} /></button>
                            </td>
                          </tr>
                        ))}
                        {templateItems.length === 0 && (
                          <tr><td colSpan={9} className="px-2 py-6 text-center text-zinc-500">No rows — add one below or re-upload the file.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={addTemplateItem} className="border-zinc-800 rounded-sm text-xs" data-testid="settings-template-add-row">+ Add Row</Button>
                    <Button size="sm" onClick={saveTemplateCorrections} disabled={templateSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs font-semibold" data-testid="settings-template-save">
                      {templateSaving ? "Saving…" : "Save Corrections"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="followup" className="mt-5 space-y-4 max-w-sm">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Auto follow-up interval (days)</label>
            <Input type="number" min="1" max="90" value={s.nudge_days} onChange={set("nudge_days")}
              className={FIELD_CLS} data-testid="settings-nudge-days" />
            <p className="text-xs text-zinc-500 mt-2">Vendors with a Sent PO pending longer than this are auto-emailed a reminder, repeated at the same interval until material arrives.</p>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Preferred manual reminder channel</label>
            <Select value={s.nudge_channel || "email"} onValueChange={(v) => setS((x) => ({ ...x, nudge_channel: v }))}>
              <SelectTrigger className={FIELD_CLS} data-testid="settings-nudge-channel"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="whatsapp">WhatsApp (requires Twilio setup)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-zinc-500 mt-2">WhatsApp sending needs Twilio credentials — until then WhatsApp reminders are only logged, not delivered.</p>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <div className="flex items-center justify-between border border-zinc-800 bg-zinc-950 rounded-sm px-3 py-3 mb-3">
              <div className="pr-4">
                <div className="text-sm text-zinc-100 font-semibold">Overdue Stage Digest</div>
                <p className="text-xs text-zinc-500 mt-1">A daily email listing every RED/overdue workflow stage company-wide, sent only to the team members you pick below.</p>
              </div>
              <Switch checked={!!digest.enabled} onCheckedChange={(v) => setDigest((d) => ({ ...d, enabled: v }))}
                className="data-[state=checked]:bg-emerald-600 shrink-0" data-testid="settings-digest-enabled" />
            </div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Recipients</label>
            <div className="border border-zinc-800 rounded-sm max-h-52 overflow-y-auto divide-y divide-zinc-900 mb-3">
              {teamMembers.map((m) => (
                <label key={m.id} className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-zinc-900/60" data-testid={`settings-digest-recipient-${m.id}`}>
                  <input type="checkbox" checked={digest.recipient_ids.includes(m.id)} onChange={() => toggleDigestRecipient(m.id)}
                    className="accent-emerald-600" />
                  <span className="text-zinc-200">{m.name}</span>
                  <span className="text-zinc-600">· {m.role}</span>
                </label>
              ))}
              {teamMembers.length === 0 && <p className="px-3 py-3 text-zinc-600">No team members found.</p>}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={saveDigest} disabled={digestSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs font-semibold" data-testid="settings-digest-save">
                {digestSaving ? "Saving…" : "Save Digest Settings"}
              </Button>
              <Button size="sm" variant="outline" onClick={sendTestDigest} className="border-zinc-800 text-zinc-300 rounded-sm text-xs" data-testid="settings-digest-test">
                Send Test Digest Now
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="manufacturing" className="mt-5 space-y-4 max-w-sm">
          <div>
            <div className="text-sm text-zinc-100 font-semibold mb-1">Manufacturing &amp; Design-Skip Alerts</div>
            <p className="text-xs text-zinc-500 mb-3">Admin and Manager always get notified when a shop-floor stage (Cutting → Testing) starts/completes, or Design/Nesting is skipped for an order. Pick specific Costing/HR people below to also receive these alerts.</p>
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Additional Recipients</label>
            <div className="border border-zinc-800 rounded-sm max-h-52 overflow-y-auto divide-y divide-zinc-900 mb-3">
              {teamMembers.map((m) => (
                <label key={m.id} className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-zinc-900/60" data-testid={`settings-mfg-recipient-${m.id}`}>
                  <input type="checkbox" checked={mfgAlerts.recipient_ids.includes(m.id)} onChange={() => toggleMfgRecipient(m.id)}
                    className="accent-emerald-600" />
                  <span className="text-zinc-200">{m.name}</span>
                  <span className="text-zinc-600">· {m.department || m.role}</span>
                </label>
              ))}
              {teamMembers.length === 0 && <p className="px-3 py-3 text-zinc-600">No team members found.</p>}
            </div>
            <Button size="sm" onClick={saveMfgAlerts} disabled={mfgAlertsSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs font-semibold" data-testid="settings-mfg-alerts-save">
              {mfgAlertsSaving ? "Saving…" : "Save Recipients"}
            </Button>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">"Slow Stage" threshold (hours)</label>
            <p className="text-xs text-zinc-500 mb-2">Used by the Manufacturing Stage Duration report to flag stages taking longer than this on average.</p>
            <div className="flex items-center gap-2">
              <Input type="number" min="1" value={slowThreshold} onChange={(e) => setSlowThreshold(e.target.value)} className={`${FIELD_CLS} max-w-[140px]`} data-testid="settings-slow-threshold" />
              <Button size="sm" onClick={saveSlowThreshold} disabled={slowThresholdSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs font-semibold" data-testid="settings-slow-threshold-save">
                {slowThresholdSaving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="order-takers" className="mt-5 space-y-4 max-w-sm">
          <div>
            <div className="text-sm text-zinc-100 font-semibold mb-1">Who Can Take Orders</div>
            <p className="text-xs text-zinc-500 mb-3">
              Pick exactly who is allowed to appear as "Order Taken By / Concerned Person" on new/edited orders — usually
              just the specific people who actually take orders from customers, not the whole team.
              Leave nothing ticked to fall back to showing everyone.
            </p>
            <div className="border border-zinc-800 rounded-sm max-h-52 overflow-y-auto divide-y divide-zinc-900 mb-3">
              {teamMembers.map((m) => (
                <label key={m.id} className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-zinc-900/60" data-testid={`settings-order-taker-${m.id}`}>
                  <input type="checkbox" checked={orderTakers.user_ids.includes(m.id)} onChange={() => toggleOrderTaker(m.id)}
                    className="accent-emerald-600" />
                  <span className="text-zinc-200">{m.name}</span>
                  <span className="text-zinc-600">· {m.department || m.role}</span>
                </label>
              ))}
              {teamMembers.length === 0 && <p className="px-3 py-3 text-zinc-600">No team members found.</p>}
            </div>
            <Button size="sm" onClick={saveOrderTakers} disabled={orderTakersSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs font-semibold" data-testid="settings-order-takers-save">
              {orderTakersSaving ? "Saving…" : "Save Order Takers"}
            </Button>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <div className="flex items-center justify-between border border-zinc-800 bg-zinc-950 rounded-sm px-3 py-3 mb-3">
              <div className="pr-4">
                <div className="text-sm text-zinc-100 font-semibold">Order Taker Reminder</div>
                <p className="text-xs text-zinc-500 mt-1">
                  Nudge the people above (or Admin/Manager if none picked) when no new customer order has been
                  logged for a while — catches orders taken by phone/in person that haven't been entered yet.
                </p>
              </div>
              <Switch checked={!!orderTakerReminder.enabled} onCheckedChange={(v) => setOrderTakerReminder((d) => ({ ...d, enabled: v }))}
                className="data-[state=checked]:bg-emerald-600 shrink-0" data-testid="settings-order-taker-reminder-enabled" />
            </div>
            <div className="flex items-center gap-2 mb-3">
              <label className="text-xs text-zinc-400 whitespace-nowrap">Remind after</label>
              <Input type="number" min="1" value={orderTakerReminder.threshold_days}
                onChange={(e) => setOrderTakerReminder((d) => ({ ...d, threshold_days: e.target.value ? Number(e.target.value) : 1 }))}
                className={`${FIELD_CLS} max-w-[90px]`} data-testid="settings-order-taker-reminder-days" />
              <span className="text-xs text-zinc-400">day(s) with no new order</span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={saveOrderTakerReminder} disabled={orderTakerReminderSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs font-semibold" data-testid="settings-order-taker-reminder-save">
                {orderTakerReminderSaving ? "Saving…" : "Save Reminder Settings"}
              </Button>
              <Button size="sm" variant="outline" onClick={sendTestOrderTakerReminder} className="border-zinc-800 text-zinc-300 rounded-sm text-xs" data-testid="settings-order-taker-reminder-test">
                Send Test Reminder Now
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="store" className="mt-5 space-y-4 max-w-lg">
          <div className="flex items-center justify-between border border-zinc-800 bg-zinc-950 rounded-sm px-3 py-3">
            <div className="pr-4">
              <div className="text-sm text-zinc-100 font-semibold">Show vendor pricing to Store</div>
              <p className="text-xs text-zinc-500 mt-1">
                Team members granted the "Material Receipts (Store)" permission (Team Members page) can always see item names
                and quantities to record what's arrived. Turn this on if they should also see vendor name, rate and PO value.
              </p>
            </div>
            <Switch checked={!!s.store_show_pricing} onCheckedChange={(v) => setS((x) => ({ ...x, store_show_pricing: v }))}
              className="data-[state=checked]:bg-emerald-600 shrink-0" data-testid="settings-store-show-pricing" />
          </div>
          <p className="text-xs text-zinc-500">Store team members use the <b>Material Receipts</b> page in the sidebar to record what's arrived against each PO line.</p>
        </TabsContent>

        <TabsContent value="ifttt" className="mt-5 space-y-4 max-w-lg">
          <div>
            <div className="text-sm text-zinc-100 font-semibold mb-1">IFTTT Webhooks</div>
            <p className="text-xs text-zinc-500 mb-3">
              Fire an IFTTT applet whenever one of these happens here — build any action you like on IFTTT's
              side (SMS, Google Sheet row, Slack message, etc). Get your key from{" "}
              <a href="https://ifttt.com/maker_webhooks" target="_blank" rel="noreferrer" className="text-emerald-400 underline">ifttt.com/maker_webhooks</a> → Documentation.
            </p>
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Webhooks Key</label>
            <div className="flex items-center gap-2 mb-1">
              <Input type="password" value={ifttKeyInput} onChange={(e) => setIfttKeyInput(e.target.value)}
                placeholder={iftt.connected ? `Connected (key ending ${iftt.key_masked})` : "Paste your IFTTT Webhooks key"}
                className={FIELD_CLS} data-testid="settings-ifttt-key" />
            </div>
            {iftt.connected && <p className="text-xs text-emerald-400 mb-3">Connected — key ending {iftt.key_masked}. Paste a new key above to replace it.</p>}
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5 mt-3">Events that fire an applet</label>
            <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-900 mb-3">
              {[
                ["order_created", "New Order Created"],
                ["po_placed", "PO Placed (status set to Sent)"],
                ["drawing_approved", "Drawing Approved"],
                ["delay_logged", "Delay Logged"],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between px-3 py-2.5" data-testid={`settings-ifttt-event-${key}`}>
                  <span className="text-xs text-zinc-200">{label}</span>
                  <Switch checked={!!iftt.events?.[key]} onCheckedChange={() => toggleIfttEvent(key)}
                    className="data-[state=checked]:bg-emerald-600" data-testid={`settings-ifttt-toggle-${key}`} />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={saveIftt} disabled={ifttSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs font-semibold" data-testid="settings-ifttt-save">
                {ifttSaving ? "Saving…" : "Save IFTTT Settings"}
              </Button>
              <Button size="sm" variant="outline" onClick={testIftt} disabled={ifttTesting || !iftt.connected} className="border-zinc-800 text-zinc-300 rounded-sm text-xs" data-testid="settings-ifttt-test">
                {ifttTesting ? "Sending…" : "Send Test Event"}
              </Button>
              {iftt.connected && (
                <Button size="sm" variant="outline" onClick={disconnectIftt} disabled={ifttDisconnecting} className="border-red-900 text-red-400 hover:bg-red-950 rounded-sm text-xs" data-testid="settings-ifttt-disconnect">
                  {ifttDisconnecting ? "Disconnecting…" : "Disconnect"}
                </Button>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
